import { ADDON_GROUP, planTypes, SWITCH_CURRENCY } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { translateToEn } from '@/plugins/i18n';
import { logger } from '@/utils/log';
import { dataLayerPayload4Rebranding } from '@zoom/billing-rebranding';
import store from '@/store';
import { pageConfig } from "@/common/service/page-config";
import { shouldUseCrossOrigin } from '@/utils/crossOriginHelper';
import { payMethodNameMap } from '@/views/checkout/payment/helper/constant';
import { getOnlinePlanName } from '@/common/product';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { routePathMap } from '@/router/constant';
import { trackingPlanTypes, PageTypes, MODAL_ENTRY_SOURCE_MAP } from '@/common/constants';
import {
  isNeedOpenPlanSelectionDialog,
  getPlanSelectionDialogPageVersion,
} from '@/plans/common/plan-selection-dialog-service';
import { cleanObj, getUrlSearchParams } from '@/utils';
import { isString, isRegExp, isFunction } from 'lodash';

let extraContext4AddOn = {};

export function setExtraContext4AddOn(context = {}) {
  extraContext4AddOn = { ...context };
}

export function getAndClearExtraContext4AddOn() {
  const context = { ...extraContext4AddOn };
  extraContext4AddOn = {};
  return context;
}

export function clearExtraContext4AddOn() {
  extraContext4AddOn = {};
}

function getReferrer() {
  const url = window?.newBillingConfig?.referrer;
  const referrerRegExp = {
    'billing': url => /\/billing/.test(url),
    'pricing': url => /\/pricing/.test(url),
  };
  return Object.keys(referrerRegExp).find(k => {
    return referrerRegExp[k](url);
  });
}

const referrer = getReferrer() || '';

export const AddOnGroupTrackingUILocation = {
  [ADDON_GROUP.JOINT_SALES]: 'special promotion',
  [ADDON_GROUP.WIN_BACK]: 'previously purchased',
  [ADDON_GROUP.BIZ_PLUS]: 'have users who only need workplace or phone?',
  [ADDON_GROUP.DEFAULT]: 'enhance your zoom experience',
}

const gaFlowTypes = {
  buyNow: 'Direct Purchase',
  basic2pro: 'Free to Paid Upgrade',
  edit: `${referrer} edit`.trim(),
  add2cart: `${referrer} new add-ons`.trim(),
  pro2biz: 'billing upgrade paid2paid',
  [flowTypes.pro2prop]: 'billing upgrade paid2paid',
  [flowTypes.pro2bundle]: 'billing upgrade paid2paid',
  [flowTypes.prop2bundle]: 'billing upgrade paid2paid',
  [flowTypes.biz2bundle]: 'billing upgrade paid2paid',
  addOnPurchase: 'Add-On Purchase',
  invoiceNoAutopay: 'Invoice No Autopay',
  invoiceWAutopay: 'Invoice with Autopay',
  trial2paid: `free trial upgrade${referrer ? ` - ${referrer} new add-ons`: ''}`,
  // BUY TRIAL - url type=buy_trial
  [flowTypes.buyTrial]: `buy free trial`,
  // FREE TRIAL - url type=add_trial
  [flowTypes.add2trial]: `add free trial${referrer ? ` - ${referrer} new add-ons`: ''}`,
  [flowTypes.zs2ze]: `upsell`,
  [flowTypes.switch]: 'cancellation - save offer'
}
export const TRACKING_ARL_TEXT = {
  ARL: 'arl',
  NON_ARL: 'non-arl',
};

const Entry_Points_URL = {
  pricing: '/pricing',
  billing: '/billing',
  user: '/account/user',
  hd: '/myhome',
  wb: '/wb/',
}
const Entry_Points_Sub_Domain = {
  wb: 'whiteboard',
};

const Entry_Points = {
  pricing: 'pricing',
  billing: 'billing',
  hd: 'hd',
  user: 'user',
  wb: 'wb',
  client: 'client',
}
export const TRACKING_CONSTANT_KEYS = {
  StandaloneInProduct: 'standalone in-product',
  SelectAPlan: 'select a plan',
  ModalCheckout: 'modal checkout',
  Web: 'web',
  DesktopClient: 'desktop client',
  MobileClient: 'mobile client',
};

export const getEntryPoint = () => {
  const referer = window?.newBillingConfig?.referrer ?? '';
  let entryPoint = '';
  if (isFromInProductPageModalCheckout()) {
    entryPoint = getInProductPageModalCheckoutEntryPoint();
    if (entryPoint) {
      return { entryPoint };
    }
  }

  // if has pageFrom parameter in path, use pageFrom as entryPoint
  // TODO: can consider using getModalCheckoutConfigs from sessionStorage to get pageFrom value, avoid getting it from path every time
  if (pageConfig?.pageFrom) {
    entryPoint = pageConfig.pageFrom;
    if (entryPoint === 'nbp') {
      entryPoint = Entry_Points.billing;
    }
  }

  // if entryPoint is not set, check referer
  if (!entryPoint && referer) {
    try {
      let refererUrl = new URL(referer),
        pathname = refererUrl.pathname
      if (pathname.startsWith(Entry_Points_URL.pricing)) {
        entryPoint = Entry_Points.pricing;
      } else if (pathname.startsWith(Entry_Points_URL.billing)) {
        entryPoint = Entry_Points.billing;
      } else if (pathname.startsWith(Entry_Points_URL.hd)) {
        entryPoint = Entry_Points.hd;
      } else if (pathname.startsWith(Entry_Points_URL.user)) {
        entryPoint = Entry_Points.user;
      } else if (pathname.startsWith(Entry_Points_URL.wb)) {
        entryPoint = Entry_Points.wb;
      }
    } catch (error) {
      logger.error(error);
    }
  }

  if (pageConfig.modalFrom === Entry_Points.client) {
    entryPoint = Entry_Points.client;
  }
  if (!entryPoint && shouldUseCrossOrigin()) {
    if (referer.includes(Entry_Points_Sub_Domain.wb)) {
      entryPoint = Entry_Points.wb;
    }
  }

  if (entryPoint) {
    return { entryPoint };
  } else {
    return {};
  }
}

export function getARlTagText() {
  const { basicConfig } = store.getters;
  const { isARLCustomer } = basicConfig;
  return `${isARLCustomer ? TRACKING_ARL_TEXT.ARL : TRACKING_ARL_TEXT.NON_ARL}`;
}
export const getTrackingFlowType = () => {
  if(window.$context.is.edit && window.$context.is.delta) return 'add license';
  if(window.$context.is.winback) return 'Past purchase winback'
  if (window.$context.is.billingBasicPlusOffer) return `cancellation ${getARlTagText()} - switch to basic plus`;
  if (window.$context.is.billingGetOffer) return `cancellation ${getARlTagText()} - save offer`;
  return gaFlowTypes[window.$context?.flow];
}

const CHARGE_AMOUNT_DISPLAY = {
  PRORATED: 'prorated',
  NORMAL: 'normal',
}

/**
 * Get ITM (In-The-Moment) tracking context from window.billingItmContext.
 * Returns an empty object when billingItmContext is absent or getItmContext is not available.
 *
 * The global window.billingItmContext is defined in the web project:
 * web-main/src/main/webapp/WEB-INF/ftl/account/billing/utils/itm_parameter_cache_utils.ftl
 *
 * @returns {Object}
 */
export const getItmTrackingPayload = () => {
  const itmContext = window.billingItmContext;
  if (!itmContext || typeof itmContext.getItmContext !== 'function') {
    return {};
  }
  const context = itmContext.getItmContext();
  return context && typeof context === 'object' ? context : {};
};

// customizable raw payload
const EventTracking = (arg) => {
  let payload = arg;
  if (typeof arg === 'function') {
    // use a modifier to reuse the payload
    const basicPayload = {
      flowType: getTrackingFlowType(),
      currencyCode: window.$context?.currency
    };
    payload = Object.assign(basicPayload, arg(basicPayload));
  }
  payload = dataLayerPayload4Rebranding?.(payload) ?? payload;
  payload = Object.assign(
    payload,
    {
      chargeAmountDisplay: window.__$app__?.$store.getters.isProrated
        ? CHARGE_AMOUNT_DISPLAY.PRORATED
        : CHARGE_AMOUNT_DISPLAY.NORMAL,
    },
    getItmTrackingPayload()
  );
  logger.table(payload);
  Array.isArray(window.dataLayer) && window.dataLayer.push(payload);
};

const basicFormatter = (...args) => {
  const [event, buyInteraction, buySelection, flowType = getTrackingFlowType()] = args;
  EventTracking({
    event,
    buyInteraction,
    buySelection,
    flowType
  });
};

EventTracking.persistentCart = (buySelection) => {
  return EventTracking({
    event: 'buyInteraction',
    buyInteraction: 'cart',
    buySelection
  })
}

EventTracking.persistentCartPopover = (buySelection) => {
  return EventTracking({
    event: 'buyInteraction',
    buyInteraction: 'cart popover',
    buySelection
  })
}

EventTracking.persistentCartPopoverBanner = (buySelection) => {
  return EventTracking({
    event: 'buyInteraction',
    buyInteraction: 'banner - cart popover',
    buySelection
  })
}

EventTracking.buyInteraction = (buyInteraction, buySelection, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType,
});

EventTracking.buyInteractionWithOptions = (buyInteraction, buySelection, flowType = getTrackingFlowType(), options = {}) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType,
  ...options
});

EventTracking.pricingInteractionWithOptions = (pricingInteraction, pricingSelection, flowType = getTrackingFlowType(), options = {}) => EventTracking({
  event: 'pricingInteraction',
  pricingInteraction,
  pricingSelection,
  flowType,
  ...options
});

EventTracking.buyInteractionWithBizInfo = (buyInteraction, buySelection,  businessPersonalAccount,
  businessSize,
  businessRegion, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction,
  buySelection,
  flowType,
  ...(businessPersonalAccount && { businessPersonalAccount }),
  ...(businessSize && { businessSize }),
  ...(businessRegion && { businessRegion }),
});

EventTracking.addOnBuyInteraction = (buyInteraction, buySelection, flowType = getTrackingFlowType()) => {
  const extraContext = getAndClearExtraContext4AddOn();

  const payload = {
    event: 'buyInteraction',
    buyInteraction,
    buySelection,
    flowType: `${flowType} add-ons`,
    ...extraContext
  };

  return EventTracking(payload);
};

EventTracking.addTrialInteraction = (products, flowType = getTrackingFlowType()) => EventTracking({
  event: 'Product View',
  flowType,
  products
});

EventTracking.addressInteractionWBizInfo = (buySelection, checkbox, businessPersonalAccount,
  businessSize,
  businessRegion, flowType = getTrackingFlowType(),
) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout address',
    buySelection,
    flowType,
    ...(businessPersonalAccount && { businessPersonalAccount }),
    ...(businessSize && { businessSize }),
    ...(businessRegion && { businessRegion }),
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.addressInteraction = (buySelection, checkbox, flowType = getTrackingFlowType()) => {
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout address',
    buySelection,
    flowType,
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.paymentInteraction = ({
  buySelection,
  checkbox,
  flowType = getTrackingFlowType(),
  withPaymentType = false,
}) => {
  const { selectedPaymentMethod } = store.getters;
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout payment method',
    buySelection,
    flowType,
    ...(withPaymentType ? { paymentType: translateToEn(payMethodNameMap[selectedPaymentMethod]) } : {}),
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.paymentInteractionWBiz = ({
  buySelection,
  businessPersonalAccount,
  businessSize,
  businessRegion,
  flowType = getTrackingFlowType(),
  checkbox,
  withPaymentType = false,
}) => {
  const { selectedPaymentMethod } = store.getters;
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'checkout payment method',
    buySelection,
    flowType,
    ...(businessPersonalAccount && { businessPersonalAccount }),
    ...(businessSize && { businessSize }),
    ...(businessRegion && { businessRegion }),
    ...(withPaymentType ? { paymentType: translateToEn(payMethodNameMap[selectedPaymentMethod]) } : {}),
  }
  if (checkbox) payload = Object.assign(payload, {checkbox});
  EventTracking(payload);
}

EventTracking.partnerLandingInteraction = (pricingSelection, uiLocation, pricingInteraction = gaFlowTypes.buyNow) => {
  let payload = {
    event: 'pricingInteraction',
    pricingInteraction,
    pricingSelection,
    uiLocation,
  }
  EventTracking(payload);
}

EventTracking.couponInteraction = (buySelection, couponCode, flowType = getTrackingFlowType()) => EventTracking({
  event: 'buyInteraction',
  buyInteraction: 'order summary',
  buySelection,
  couponCode,
  flowType,
});

EventTracking.orderSummary = (buySelection) => basicFormatter('buyInteraction', 'order summary', buySelection)

EventTracking.sign = (accountSelection, accountMethod, modifier) => {
  let payload = {
    event: 'accountInteraction',
    accountInteraction: 'checkout sign up',
    accountSelection,
    flowType: gaFlowTypes[window.$context?.flow],
    accountMethod,
  };
  if (modifier) payload = Object.assign(payload, modifier(payload));
  EventTracking(payload);
}

EventTracking.paymentSwitchType = (paymentType, businessPersonalAccount, businessSize, businessRegion) =>
  EventTracking({
    event: 'Checkout Select Payment Type',
    paymentType,
    businessPersonalAccount,
    businessSize,
    businessRegion,
    flowType: getTrackingFlowType(),
  });

EventTracking.postPurchase = (buySelection) => basicFormatter('buyInteraction', 'purchase confirmation', buySelection);

EventTracking.formValidationError = (buySelection)=>{
  let payload = {
    event: 'buyInteraction',
    buyInteraction: 'ui error',
    buySelection,
    flowType:  getTrackingFlowType(),
  };
  EventTracking(payload);
}

EventTracking.triggerUIError = (buySelection) => EventTracking({
  event: 'buyInteraction',
  buyInteraction: 'ui error',
  buySelection,
  flowType: getTrackingFlowType(),
});

EventTracking.triggerPollingError = (buySelection) => EventTracking({
  event: 'buyInteraction',
  buyInteraction: 'api error',
  buySelection,
  flowType: getTrackingFlowType(),
});

EventTracking.pageLoad = () => EventTracking({
  event: 'pageLoad',
  ...pageLoadTracking(),
})

function pageLoadTracking() {
  // window.$context.is is not ready yet, so we need to check the url path instead
  const isInWhichPage = {
    config: location.pathname.endsWith(routePathMap.config),
    checkout: location.pathname.endsWith(routePathMap.checkout),
    edit: location.pathname.endsWith(routePathMap.edit),
    add: location.pathname.endsWith(routePathMap.add),
    mix: location.pathname.endsWith(routePathMap.mix),
    downgrade: location.pathname.endsWith(routePathMap.downgrade),
    upgrade: location.pathname.endsWith(routePathMap.upgrade),
    updateSuccess: location.pathname.endsWith(routePathMap.updateSuccess),
    addTrialSuccess: location.pathname.endsWith(routePathMap.addTrialSuccess),
    apmSuccess: location.pathname.endsWith(routePathMap.apmSuccess),
    success: location.pathname.endsWith(routePathMap.success),
    currencySwitch: (new RegExp('(operation|type)=' + SWITCH_CURRENCY)).test(location.search)
  }
  const isSuccessPage = isInWhichPage.updateSuccess || isInWhichPage.addTrialSuccess || isInWhichPage.apmSuccess || isInWhichPage.success;
  function getPageCategoryProduct() {
    const fallbackDefault = 'product';
    const category = (() => {
      if (isInWhichPage.add || isSuccessPage) {
        return pageConfig?.from_sub || pageConfig?.from
      } else if (isInWhichPage.upgrade) {
        return pageConfig?.editConfig?.planType;
      } else {
        return pageConfig?.plan ||
          pageConfig?.editConfig?.plan ||
          pageConfig?.editConfig?.category;
      }
    })();
    if (isInWhichPage.currencySwitch) {
      return undefined;
    } else if (!category) {
      // If we cannot even determine category from URL / pageConfig,
      // keep the historical default value for compatibility.
      return fallbackDefault;
    }
    try {
      const planType = getPlanTypeByCategory(category);
      const matchedCategoryProduct = getTrackingProductNameByPlanType(planType);
      if (matchedCategoryProduct) {
        return matchedCategoryProduct;
      }

      // Fallback to raw category (plan/category from URL)
      return category;
    } catch (error) {
      logger.error(error);
      // On error, still fallback to category string
      return category;
    }
  }
  function getPageCategorySub() {
    const categorySubMap = {
      'currency switch': isInWhichPage.checkout && isInWhichPage.currencySwitch,
      'currency switch success': isInWhichPage.currencySwitch && isInWhichPage.updateSuccess,
      config: isInWhichPage.config,
      checkout: isInWhichPage.checkout && !isInWhichPage.currencySwitch,
      edit: isInWhichPage.edit,
      add: isInWhichPage.add,
      mix: isInWhichPage.mix,
      downgrade: isInWhichPage.downgrade,
      upgrade: isInWhichPage.upgrade,
      'update success': isInWhichPage.updateSuccess && !isInWhichPage.currencySwitch,
      'add trial success': isInWhichPage.addTrialSuccess,
      'apm success': isInWhichPage.apmSuccess,
      'purchase success': isInWhichPage.success
    }
    const currentCategorySub = Object.keys(categorySubMap).find(key => categorySubMap[key] === true);
    return currentCategorySub || undefined;
  }
  function getPageNameAndVersion() {
    let pageVersion,
      pageName = 'billing';
    // the page version is mainly defined in web ftl page
    // but plan selection dialog is a special case, we need to handle it here
    if (isNeedOpenPlanSelectionDialog()) {
      pageVersion = getPlanSelectionDialogPageVersion();
    } else if (isFromInProductPageModalCheckout()) {
      pageVersion = getInProductPageModalCheckoutPageVersion();
    }
    // override pageVersion if it is set in extraPageLoadFields
    if (pageVersion && window?.extraPageLoadFields?.pageVersion) {
      window.extraPageLoadFields.pageVersion = pageVersion;
    }
    return { pageVersion, pageName };
  }
  const pageLoadingPayload = {
    pageCategory: 'buy flow',
    pageCategoryProduct: getPageCategoryProduct(),
    pageCategorySub: getPageCategorySub(),
    ...getPageNameAndVersion(),
  };
  return cleanObj(pageLoadingPayload);
}

export const proratedProductQuantityModifier = (product, x) => {
  if(window.$context?.is?.edit && store.getters.getSelectedPlans.length === 1 && store.getters.isProrated) {
    const boughtPlansCodeMapQuantity = store.getters.boughtPlans.reduce((acc, x) => {
      acc[x.plan_code] = x?.cartInfo?.selected_number;
      return acc;
    }, {});
    const boughtNum = boughtPlansCodeMapQuantity[x?.plan_code]??0;
    if(boughtNum > 0){
      return {
        quantity: product?.quantity - boughtNum,
      }
    }
  }
  return {};
}

// Special product naming rules configuration
// Add new special cases here when needed
const SPECIAL_PRODUCT_NAMES = {
  // Zoom Phone Pay As You Go
  ZOOM_PHONE_PAYG: {
    condition: (plan) => plan?.type === planTypes.ZOOM_PHONE && plan?.is_payg,
    name: "Zoom Phone Pay As You Go"
  },
};

/**
 * Get special product name based on plan configuration
 * @param {Object} plan - Plan object
 * @returns {string} - Special product name or empty string
 */
const getSpecialProductName = (plan) => {
  for (const config of Object.values(SPECIAL_PRODUCT_NAMES)) {
    if (config.condition(plan)) {
      return config.name;
    }
  }
  return '';
};

/**
 * Create trial product data for tracking
 * @param {Object} product - Base product object
 * @param {Object} plan - Plan object with trial information
 * @param {string} specialProductName - Special product name if applicable
 * @param {boolean} includeTrialCode - Whether to include trialCode field (for BUY TRIAL flow)
 * @returns {Object} - Trial product object
 */
const createTrialProduct = (product, plan, specialProductName, includeTrialCode = false) => {
  const planName = getOnlinePlanName(plan, true);

  const trialProduct = {
    ...product,
    // brand: plan?.type,
    category: plan?.category,
    // Use special name for specific products (e.g., Zoom Phone Pay As You Go)
    // Otherwise, construct name from brand/plan name + "Free Trial"
    name: specialProductName || `${planName} Free Trial`,
    id: plan?.isOnlineFreeTrial ? `${plan?.plan_code}_free_trial`: plan?.plan_code,
    // get standard price (confirmed by anv.vu ZOOM-1102549)
    price: product?.price,
  };

  // BUY TRIAL flow includes trialCode field
  if (includeTrialCode) {
    trialProduct.trialCode = plan?.isOnlineFreeTrial ? plan?.trialCode : '';
  }

  return trialProduct;
};

export const mapProducts = (plans, modifier = o => o) => {
  const flowType = getTrackingFlowType();

  return plans.map(x => {
    let brand = x?.typeNameKey ? translateToEn(x.typeNameKey) : '';

    // Handle Zoom Phone add-ons brand
    if ([planTypes.ZOOM_PHONE$ZPA, planTypes.ZOOM_PHONE$ZPI].includes(x.type)) {
      brand = "Zoom Phone add-ons"
    }

    // Get special product name from configuration
    const specialProductName = getSpecialProductName(x);

    let product = {
      brand,
      category: flowType,
      name: x?.planNameKey ? translateToEn(x.planNameKey, [x?.capacity]) : '',
      id: x?.plan_code,
      price: x?.amount / 100,
      list: flowType,
      quantity: x?.cartInfo?.selected_number,
    };

    // FREE TRIAL - different product data
    if(flowType === gaFlowTypes[flowTypes.add2trial]) {
      product = createTrialProduct(product, x, specialProductName, false);
    }

    // BUY TRIAL - different product data (includes trialCode)
    if(flowType === gaFlowTypes[flowTypes.buyTrial]) {
      product = createTrialProduct(product, x, specialProductName, true);
    }

    return Object.assign(product, modifier(product, x));
  });
};

// this is used to map the plan type to the tracking product name
// naming rules:
// 1. all product names should be in english
// 2. no underscore in the product name
const TRACKING_PRODUCT_NAME_MAP = {
  'zoom workplace': [planTypes.ZOOM_MEETING, planTypes.ZOOM_ONE, planTypes.ZONE_BASIC_PLUS],
  'zoom workplace pro plus': [planTypes.ZONE_PRO_PLUS],
  'zoom workplace business plus': [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_BIZ_PLUS],
  'zoom united': [planTypes.ZOOM_UNITED],
  'zoom team chat': [planTypes.ZOOM_CHAT],
  'zoom phone': [planTypes.ZOOM_PHONE, planTypes.ZOOM_PHONE$ADDITIONAL, planTypes.ZOOM_PHONE_HARDWARE],
  'zoom phone power pack': [planTypes.ZOOM_PHONE$ZPA],
  'international select add-on': [planTypes.ZOOM_PHONE$ZPI],
  'zoom virtual agent receptionist': [planTypes.ZOOM_PHONE$AICONCIERGE],
  'phone number': [planTypes.PHONE_NUMBER],
  'desk phones': [planTypes.ZOOM_DEVICE$DESK_PHONE],
  headset: [planTypes.ZOOM_DEVICE$HEADSET],
  'zoom rooms': [planTypes.ZOOM_ROOMS],
  'zoom whiteboard': [planTypes.ZOOM_WHITEBOARD],
  'zoom whiteboard plus': [planTypes.ZOOM_WHITEBOARD_PLUS],
  'zoom scheduler': [planTypes.ZOOM_SCHEDULER],
  'zoom docs': [planTypes.ZOOM_DOCS],
  'zoom clips': [planTypes.ZOOM_CLIPS],
  'zoom video management': [planTypes.VIDEO_MANAGEMENT],
  'zoom workspace reservation': [planTypes.WORKSPACE_RESERVATION],
  'zoom visitor management': [planTypes.VISITOR_MANAGEMENT],
  'zoom contact center': [planTypes.ZOOM_CONTACT_CENTER, planTypes.ZVA],
  'zoom events': [planTypes.ZOOM_EVENTS, planTypes.EVENT_SAAS],
  'zoom sessions': [planTypes.EVENT_SAAS_CORE],
  additional_host: [planTypes.ADDITIONAL_HOST],
  additional_co_editor: [planTypes.ADDITIONAL_CO_EDITOR],
  'zoom webinars': [planTypes.WEBINAR],
  'webinar promo': [planTypes.WEBINAR_PROMO],
  'zoom revenue accelerator': [planTypes.ZOOM_REVENUE_ACCELERATOR],
  'Concurrent Meeting Basic': [planTypes.CONCURRENCY_MEETING_BASIC],
  'Concurrent Meeting Plus': [planTypes.CONCURRENT_MEETING_PLUS],
  'custom avatars': [planTypes.CUSTOM_AVATARS],
  'zoom ai companion': [planTypes.ZM_AI_COMPANION],
  'zoom custom ai companion': [planTypes.ZOOM_CUSTOM_AI_COMPANION],
  'paid developers': [planTypes.PAID_DEVELOPER],
  api: [planTypes.API],
  'zoom compliance manager powered by theta lake': [planTypes.ZCM],
  'zoom compliance manager powered by theta lake - additional storage': [planTypes.ZCM_STORAGE],
  'zoom node': [planTypes.ZOOM_NODE],
  'large meeting': [planTypes.LARGE_MEETING],
  'cloud storage': [planTypes.CMR],
  'room connector': [planTypes.ROOM_CONNECTOR],
  'zoom audio conferencing': [planTypes.TOLLFREE],
  'Zoom Audio Conferencing add-ons Dedicated Dial-In Numbers': [planTypes.TOLLFREE$DID],
  'video sdk': [planTypes.VIDEO_SDK],
  'zoom sdk universal credit': [planTypes.VIDEO_SDK_UC],
  'zoom hardware': [planTypes.ZOOM_HARDWARE],
  'hipaa compliance': [planTypes.HIPAA_COMPLIANCE],
  premier: [planTypes.PREMIER],
  'zoom iq': [planTypes.ZOOM_IQ],
  mio: [planTypes.MIO],
  translation: [planTypes.TRANSLATION],
  'translation captions': [planTypes.TRANSLATION_CAPTIONS],
  'video sdk translation': [planTypes.VIDEO_SDK_TRANSLATION],
  'zoom room hardware': [planTypes.ZOOM_ROOM_HARDWARE],
  'zoom mesh': [planTypes.ZOOM_MESH],
  'zoom customer managed key': [planTypes.CMK],
  'zoom video sdk recording ': [planTypes.LDKCMR],
  'video sdk room connector': [planTypes.LDKCRC],
  'quality streaming service': [planTypes.QSS],
  'zoom enhanced media': [planTypes.ZOOM_ENHANCED_MEDIA],
  'zoom email calendar': [planTypes.ZOOM_EMAIL_CALENDAR],
  'free trial': [planTypes.FREE_TRIAL],
};

export const getTrackingProductNameByPlanType = (planType) => {
  // planType can be either planTypes value (e.g. 'webinar') or key (e.g. 'WEBINAR'); trackingPlanTypes uses the key
  const trackingProductName = Object.keys(TRACKING_PRODUCT_NAME_MAP).find(
    (key) =>
      TRACKING_PRODUCT_NAME_MAP[key].includes(planType) || TRACKING_PRODUCT_NAME_MAP[key].includes(planTypes[planType]),
  );
  console.assert(trackingProductName, `tracking product name not found for plan type: ${planType}`);
  const result = trackingProductName || trackingPlanTypes[planType] || planType || 'unknown';
  return result?.toLowerCase();
}

const ITM_PLATFORM = {
  WEB: 'web',
  DESKTOP_CLIENT: 'desktop client',
  MOBILE_CLIENT: 'mobile client',
}

/**
 * Resolve platform from the URL search param matching /platform/i.
 * Returns 'desktop_client' if value contains 'desktop', 'mobile_client' if 'mobile', else 'web'.
 * Defaults to 'web' when the param is missing or unrecognized.
 * @returns {string} One of ITM_PLATFORM.WEB, ITM_PLATFORM.DESKTOP_CLIENT, ITM_PLATFORM.MOBILE_CLIENT
 */
export function getPlatformFromUrl() {
  const platform = getUrlSearchParams(/platform/i);
  if (platform) {
    if (platform.toLowerCase().includes('desktop')) {
      return ITM_PLATFORM.DESKTOP_CLIENT;
    } else if (platform.toLowerCase().includes('mobile')) {
      return ITM_PLATFORM.MOBILE_CLIENT;
    } else if (platform.toLowerCase().includes('web')) {
      return ITM_PLATFORM.WEB;
    }
  }
  return ITM_PLATFORM.WEB;
}

// Map plan type (key) to pageFrom value(s). Use planTypes.ZOOM_* keys so computed keys are defined.
const IN_PRODUCT_PAGE_FROM_LIST = {
  [planTypes.ZOOM_CLIPS]: 'clips',
  [planTypes.ZOOM_SCHEDULER]: 'scheduler',
  [planTypes.ZOOM_DOCS]: ['docs', 'docs-plus'],
  [planTypes.ZOOM_WHITEBOARD]: ['whiteboard', 'wb'],
};

Object.defineProperties(IN_PRODUCT_PAGE_FROM_LIST, {
  getKeyByValue: {
    writable: false,
    enumerable: false,
    value: function (value) {
      if (!value || !isString(value)) return null;
      const planKeys = Object.keys(this);
      for (let i = 0; i < planKeys.length; i++) {
        const planKey = planKeys[i];
        const matchList = this[planKey];
        if (typeof matchList === 'function') continue; // skip getKeyByValue
        let matchResult = false;
        if (Array.isArray(matchList)) {
          matchResult = matchList.includes(value);
        } else if (isString(matchList)) {
          matchResult = matchList === value;
        } else if (isRegExp(matchList)) {
          matchResult = matchList.test(value);
        } else if (isFunction(matchList)) {
          matchResult = matchList(value);
        }
        if (matchResult) return planKey;
      }
      return null;
    },
  },
});

/**
 * Returns true only when the billing page is opened via modal checkout from an in-product page.
 * "In-product" means product pages that have their own independent second-level domain (e.g. clips, scheduler).
 * Currently only clips and scheduler flows are treated as in-product modal checkout;
 * pageFrom must match IN_PRODUCT_PAGE_FROM_LIST and entrySource must be in MODAL_ENTRY_SOURCE_MAP.
 */
function isFromInProductPageModalCheckout() {
  const { pageType, pageFrom, entrySource } = pageConfig;
  return (
    pageType === PageTypes.modal &&
    !!IN_PRODUCT_PAGE_FROM_LIST.getKeyByValue(pageFrom) &&
    Object.values(MODAL_ENTRY_SOURCE_MAP).includes(entrySource)
  );
}

export function getInProductPageModalCheckoutEntryPoint() {
  if (!isFromInProductPageModalCheckout()) {
    return null;
  }
  const fromProductPlanType = IN_PRODUCT_PAGE_FROM_LIST.getKeyByValue(pageConfig.pageFrom);
  const trackingProductName = getTrackingProductNameByPlanType(fromProductPlanType);
  if (trackingProductName) {
    return [trackingProductName, TRACKING_CONSTANT_KEYS.StandaloneInProduct, getPlatformFromUrl()].filter(Boolean).join(' ');
  } else {
    return null;
  }
}

function getInProductPageModalCheckoutPageVersion() {
  return [TRACKING_CONSTANT_KEYS.StandaloneInProduct, getPlatformFromUrl(), TRACKING_CONSTANT_KEYS.ModalCheckout]
    .filter(Boolean)
    .join(' ');
}

export default EventTracking;
